import React from 'react';
import { Box, Typography, TextField, Button, Grid, Autocomplete, CircularProgress, Snackbar, Alert, FormControlLabel, Checkbox } from '@mui/material';
import { LocationOn, Save } from '@mui/icons-material';
import { AddressFormProps } from '../types/type';

export const AddressForm: React.FC<AddressFormProps> = ({
  selectedAddress,
  cityOptions,
  isGeocodingLoading,
  onAddressInputChange,
  onCitySearch,
  onCitySelect,
  onOpenMapPicker,
  onSaveAddress,
  onPrimaryChange
}) => {
  if (!selectedAddress) {
    return (
      <Typography variant="body1" sx={{ textAlign: 'center', mt: 4 }}>
        No address selected
      </Typography>
    );
  }

  return (
    <Box sx={{ flex: 1 }}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label="First Name"
            size="small"
            value={selectedAddress.addresseeFirstName || ''}
            onChange={(e) => onAddressInputChange('addresseeFirstName', e.target.value)}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label="Middle Name"
            size="small"
            value={selectedAddress.addresseeMiddleName || ''}
            onChange={(e) => onAddressInputChange('addresseeMiddleName', e.target.value)}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label="Last Name"
            size="small"
            value={selectedAddress.addresseeLastName || ''}
            onChange={(e) => onAddressInputChange('addresseeLastName', e.target.value)}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Unit"
            size="small"
            value={selectedAddress.unit || ''}
            onChange={(e) => onAddressInputChange('unit', e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Building"
            size="small"
            value={selectedAddress.building || ''}
            onChange={(e) => onAddressInputChange('building', e.target.value)}
          />
        </Grid>
      </Grid>

      <TextField
        fullWidth
        label="Street"
        size="small"
        value={selectedAddress.street || ''}
        onChange={(e) => onAddressInputChange('street', e.target.value)}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Subdivision"
        size="small"
        value={selectedAddress.subdivision || ''}
        onChange={(e) => onAddressInputChange('subdivision', e.target.value)}
        sx={{ mb: 2 }}
      />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={4}>
          <Autocomplete
            options={cityOptions}
            getOptionLabel={(option) => option.name}
            onInputChange={(event, value) => onCitySearch(value)}
            onChange={onCitySelect}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Municipality"
                size="small"
                fullWidth
                error={!selectedAddress?.city?.cityId}
                helperText={!selectedAddress?.city?.cityId ? 'Please select a valid city' : ''}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isGeocodingLoading && <CircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            value={selectedAddress?.city ? { cityId: selectedAddress.city.cityId, name: selectedAddress.city.name, provinceId: 0, provinceName: '' } : null}
            isOptionEqualToValue={(option, value) => option.cityId === value?.cityId}
            disabled={isGeocodingLoading}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label="Province"
            size="small"
            value={selectedAddress.province.name || ''}
            onChange={(e) => onAddressInputChange('province', e.target.value)}
            disabled
            InputProps={{
              readOnly: isGeocodingLoading,
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label="Zip Code"
            size="small"
            value={selectedAddress.zipCode || ''}
            onChange={(e) => onAddressInputChange('zipCode', e.target.value)}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Latitude"
            size="small"
            type="number"
            value={selectedAddress.coordinatesLatitude || ''}
            onChange={(e) => onAddressInputChange('coordinatesLatitude', e.target.value)}
            disabled
            InputProps={{
              readOnly: isGeocodingLoading,
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Longitude"
            size="small"
            type="number"
            value={selectedAddress.coordinatesLongitude || ''}
            onChange={(e) => onAddressInputChange('coordinatesLongitude', e.target.value)}
            disabled
            InputProps={{
              readOnly: isGeocodingLoading,
            }}
          />
        </Grid>
      </Grid>

      <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Checkbox
          checked={selectedAddress.isPrimary || false}
          onChange={(e) => onPrimaryChange(e.target.checked)}
          color="primary"
          sx={{ p: 0, mt: 0.5 }}
        />
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Is Primary?
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Set this as the primary address of this user.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 'auto' }}>
        <Button
          startIcon={<LocationOn />}
          variant="contained"
          sx={{ color: 'white' }}
          onClick={onOpenMapPicker}
        >
          PICK ADDRESS
        </Button>
        <Button
          startIcon={<Save />}
          variant="contained"
          sx={{ color: 'white' }}
          onClick={onSaveAddress}
        >
          SAVE
        </Button>
      </Box>
    </Box>
  );
};